import React, { useState, useEffect } from "react";
import {
  ChevronDownIcon,
  XMarkIcon,
  MapPinIcon,
} from "@heroicons/react/24/outline";

const LocationSelector = ({
  value = { primaryLocation: "", subLocations: [] },
  locations,
  onChange,
  disabled,
}) => {
  // console.log(locations);

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  // Local selection for dropdown
  const [tempSelected, setTempSelected] = useState([]);

  // Keep tempSelected in sync with parent value
  useEffect(() => {
    const all = [];
    if (value.primaryLocation) all.push(value.primaryLocation);
    if (value.subLocations && value.subLocations.length)
      all.push(...value.subLocations);
    setTempSelected(all);
  }, [value]);

  // Filter out already selected locations
  const filteredLocations = locations?.filter(
    (location) =>
      location.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
      !tempSelected.some((selected) => selected._id === location._id)
  );

  // Add or remove from temp selection
  const handleLocationToggle = (location) => {
    setTempSelected((prev) => {
      const isSelected = prev.some((selected) => selected._id === location._id);
      if (isSelected) {
        return prev.filter((selected) => selected._id !== location._id);
      } else {
        return [...prev, location];
      }
    });
  };

  const handleSelectAll = () => {
    setTempSelected((prev) => [
      ...prev,
      ...filteredLocations.filter(
        (location) => !prev.some((selected) => selected._id === location._id)
      ),
    ]);
  };

  // Commit selection to parent and close dropdown
  const handleAddSelected = () => {
    if (onChange) {
      const [primary, ...subs] = tempSelected;
      onChange({
        primaryLocation: primary || "",
        subLocations: subs,
      });
    }
    setIsDropdownOpen(false);
    setSearchTerm("");
  };

  // Remove location from selection
  const handleDeleteLocation = (locationId) => {
    const all = [];
    if (value.primaryLocation && value.primaryLocation._id !== locationId)
      all.push(value.primaryLocation);
    const subs = value.subLocations.filter((loc) => loc._id !== locationId);
    all.push(...subs);
    const [primary, ...rest] = all;
    if (onChange) {
      onChange({
        primaryLocation: primary || "",
        subLocations: rest,
      });
    }
  };

  // Move selected location to front to make it primary
  const handleSetAsPrimary = (locationId) => {
    const all = [];
    if (value.primaryLocation) all.push(value.primaryLocation);
    if (value.subLocations && value.subLocations.length)
      all.push(...value.subLocations);
    const locationToMove = all.find((location) => location._id === locationId);
    const otherLocations = all.filter(
      (location) => location._id !== locationId
    );
    if (onChange) {
      onChange({
        primaryLocation: locationToMove,
        subLocations: otherLocations,
      });
    }
  };

  // Compose display array for rendering
  const allLocations = [];
  if (value.primaryLocation) allLocations.push(value.primaryLocation);
  if (value.subLocations && value.subLocations.length)
    allLocations.push(...value.subLocations);

  return (
    <div className="w-full relative mx-auto bg-white">
      {/* Selected locations list */}
      {allLocations.length > 0 && (
        <div className="space-y-2 mb-2">
          {allLocations.map((location, index) => (
            <div
              key={location._id}
              className="flex items-center justify-between py-1 px-3 bg-gray-50 border border-gray-200 rounded-md"
            >
              <div className="flex items-center">
                {index === 0 && (
                  <MapPinIcon className="w-4 h-4 text-gray-600 mr-2" />
                )}
                <span className="text-sm text-gray-900">
                  {location.name}
                  {index === 0 && (
                    <span className="ml-2 px-2 py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                      Primary
                    </span>
                  )}
                </span>
              </div>
              <div className="flex items-center space-x-2">
                {index !== 0 && (
                  <button
                    type="button"
                    onClick={() => handleSetAsPrimary(location._id)}
                    className="px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded hover:bg-gray-100"
                    disabled={disabled}
                  >
                    Set as Primary
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => handleDeleteLocation(location._id)}
                  className="p-1 text-gray-400 hover:text-red-600"
                  disabled={disabled}
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add locations button */}
      <button
        type="button"
        onClick={() => setIsDropdownOpen((open) => !open)}
        className="w-full mt-2 flex items-center justify-between px-4 py-2 text-sm font-medium text-gray-600 bg-gray-50 border border-gray-200 rounded-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-500"
        disabled={disabled}
      >
        Add locations
        <ChevronDownIcon
          className={`w-4 h-4 transition-transform ${
            isDropdownOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {/* Dropdown */}
      {isDropdownOpen && (
        <div className="absolute left-0 right-0 mt-1 bg-white border border-gray-200 rounded-md shadow-lg z-10">
          {/* Search */}
          <div className="p-3 border-b border-gray-200">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
            />
          </div>

          {/* Select all */}
          <div className="p-3 border-b border-gray-200">
            <button
              type="button"
              onClick={handleSelectAll}
              className="text-sm text-gray-600 hover:text-gray-800"
              disabled={filteredLocations.length === 0}
            >
              Select all locations ({filteredLocations.length})
            </button>
          </div>

          {/* Location list */}
          <div className="max-h-40 overflow-y-auto">
            {filteredLocations.map((location) => (
              <label
                key={location._id}
                className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={tempSelected.some(
                    (selected) => selected._id === location._id
                  )}
                  onChange={() => handleLocationToggle(location)}
                  className="w-4 h-4 text-gray-600 border-gray-300 rounded focus:ring-gray-500"
                />
                <span className="ml-3 text-sm text-gray-900">
                  {location.name}
                </span>
              </label>
            ))}
          </div>

          {/* Add button */}
          <div className="p-3 border-t border-gray-200">
            <button
              type="button"
              onClick={handleAddSelected}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-500"
            >
              Add locations ({tempSelected.length})
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationSelector;
